import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Amplify } from 'aws-amplify';

import './i18n/index';
import './styles/global.css';
import './styles/static-pages.css';
import App from './App';

// ---------------------------------------------------------------------------
// Runtime version detection — detects when frontend has been deployed
// ---------------------------------------------------------------------------
// This solves the "alternating layout" bug by detecting when the backend
// is serving a different version than what's cached in the browser.
// Without this, users see old code until they hard-refresh.

let appVersion: string | null = null;

async function initializeVersionCheck() {
  try {
    const response = await fetch('/version.json', { cache: 'no-store' });
    const data = await response.json();
    appVersion = data.timestamp;
    console.log('[Boot] Frontend version:', data.version);
  } catch (e) {
    console.debug('[Boot] Version check failed (expected if version.json not deployed yet):', e);
  }
}

// Check every 60 seconds if version has changed (indicates new deployment)
function startVersionWatcher() {
  setInterval(async () => {
    try {
      const response = await fetch('/version.json', { cache: 'no-store' });
      const data = await response.json();
      const newVersion = data.timestamp;
      
      if (appVersion && appVersion !== newVersion) {
        console.warn('[Boot] Frontend has been updated to version', data.version);
        // Log warning but don't auto-reload — let user decide
        // You can add a toast notification here if desired
      }
      appVersion = newVersion;
    } catch (e) {
      // Silently ignore — network error or file doesn't exist
    }
  }, 60_000);
}

// Initialize version check immediately
initializeVersionCheck();

// ---------------------------------------------------------------------------
// AWS Amplify Gen 2 — external Cognito User Pool (manual configuration)
// ---------------------------------------------------------------------------

// In production (Docker), docker-entrypoint.sh runs envsubst on index.html,
// replacing ${VITE_*} tokens in window.__radiriConfig with real values.
// In local dev (npm run dev), envsubst never runs so the tokens remain as
// literal strings like "${VITE_COGNITO_USER_POOL_ID}".  resolve() detects
// that and falls back to import.meta.env.VITE_* (populated from .env.local).
const _cfg = (window as Window & { __radiriConfig?: Record<string, string> }).__radiriConfig ?? {};
const resolve = (runtime: string | undefined, dev: string | undefined): string => {
  if (runtime && !runtime.startsWith('${')) return runtime;
  return dev ?? '';
};

const userPoolId = resolve(_cfg.COGNITO_USER_POOL_ID, import.meta.env.VITE_COGNITO_USER_POOL_ID);
const userPoolClientId = resolve(_cfg.COGNITO_CLIENT_ID, import.meta.env.VITE_COGNITO_CLIENT_ID);
const oauthDomain = resolve(_cfg.COGNITO_OAUTH_DOMAIN, import.meta.env.VITE_COGNITO_OAUTH_DOMAIN);
const redirectSignIn =
  resolve(_cfg.COGNITO_REDIRECT_SIGN_IN, import.meta.env.VITE_COGNITO_REDIRECT_SIGN_IN) ||
  window.location.origin + '/';
const redirectSignOut =
  resolve(_cfg.COGNITO_REDIRECT_SIGN_OUT, import.meta.env.VITE_COGNITO_REDIRECT_SIGN_OUT) ||
  window.location.origin + '/';

// Debug: show where values came from
console.log('[Boot] Config source:', {
  userPoolId: _cfg.COGNITO_USER_POOL_ID ? 'from window.__radiriConfig' : (import.meta.env.VITE_COGNITO_USER_POOL_ID ? 'from import.meta.env' : 'EMPTY'),
  userPoolClientId: _cfg.COGNITO_CLIENT_ID ? 'from window.__radiriConfig' : (import.meta.env.VITE_COGNITO_CLIENT_ID ? 'from import.meta.env' : 'EMPTY'),
  oauthDomain: _cfg.COGNITO_OAUTH_DOMAIN ? 'from window.__radiriConfig' : (import.meta.env.VITE_COGNITO_OAUTH_DOMAIN ? 'from import.meta.env' : 'EMPTY'),
});

/** The application display name, overridable via VITE_APP_NAME at container startup. */
export const appName: string =
  resolve(_cfg.APP_NAME, import.meta.env.VITE_APP_NAME) || 'Radiri';

// Strip stale OAuth params before Amplify.configure() runs — if the PKCE
// verifier is missing from localStorage, any code in the URL is unexchangeable
// and will cause a 400 on every single page load.
if (window.location.search.includes('?code=') || window.location.search.includes('&code=')) {
  // Check if Amplify stored a PKCE verifier for this client.
  const hasPkceVerifier = Object.keys(localStorage).some(
    (k) => k.includes('pkce') || k.includes('code_verifier') || k.includes('oauth')
  );
  if (!hasPkceVerifier) {
    console.warn('[Auth] Stale ?code= in URL with no PKCE verifier — stripping before configure()');
    const clean = new URL(window.location.href);
    clean.searchParams.delete('code');
    clean.searchParams.delete('state');
    window.history.replaceState({}, '', clean.toString());
  }
}
console.log('[Boot] Cognito config check:', {
  userPoolId: userPoolId ? '✓ set' : '✗ MISSING',
  userPoolClientId: userPoolClientId ? '✓ set' : '✗ MISSING',
  oauthDomain: oauthDomain ? '✓ set' : '✗ MISSING',
  redirectSignIn: redirectSignIn ? '✓ set' : '✗ MISSING',
  redirectSignOut: redirectSignOut ? '✓ set' : '✗ MISSING',
});

if (userPoolId && userPoolClientId) {
  console.log('[Boot] Configuring Amplify with Cognito');
  Amplify.configure({
    Auth: {
      Cognito: {
        userPoolId,
        userPoolClientId,
        loginWith: {
          oauth: {
            domain: oauthDomain,
            scopes: ['openid', 'email', 'aws.cognito.signin.user.admin'],  // aws.cognito.signin.user.admin required for WebAuthn credential management — must be enabled in the Cognito app client OAuth scopes
            redirectSignIn: [redirectSignIn],
            redirectSignOut: [redirectSignOut],
            responseType: 'code', // Authorization Code Flow (PKCE-protected)
          },
        },
      },
    },
  });
} else {
  console.error('[Boot] ⚠️  Cognito NOT configured — userPoolId or userPoolClientId missing. Login will not work. Check that VITE_COGNITO_* env vars were passed to the container and properly substituted by docker-entrypoint.sh');
}

// ---------------------------------------------------------------------------
// TanStack Query client
// ---------------------------------------------------------------------------
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      retry: 1,
    },
  },
});

// ---------------------------------------------------------------------------
// Mount
// ---------------------------------------------------------------------------
const container = document.getElementById('root');
if (!container) throw new Error('Root element #root not found');

createRoot(container).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </StrictMode>,
);

// Start background version checker after React mounts (catches deployment updates)
startVersionWatcher();
